<template>
  <div class='infrastructure'>
    <div class='infrastructure__headers'>
      <div class="infrastructure__headers__filters_row">
        <filtered-search
          class='search-filter'
          @searchInput="searchChange"
          @filtersInput="filtersChange"
          :show-filters="true"
          :filter-items='filterItems'
          :filters="filters"
          tab="infrastructure"
        />
        <custom-tooltip popper-class="light-tooltip">
          <div slot='buttonContent'>
            <v-switch
              class='filter-switch'
              v-model="isShowOnlyPorts"
              :label="$t('infrastructure.ports.only')"
              @change='toggleChanged'
            ></v-switch>
          </div>
          <div slot="content">
            <slot name="tooltip">
              {{$t('infrastructure.ports.only.tooltip')}}
            </slot>
          </div>
        </custom-tooltip>
        <custom-tooltip popper-class="light-tooltip">
          <div slot='buttonContent'>
            <v-switch
              class='filter-switch'
              v-model="isShowOnlyVulnerable"
              :label="$t('infrastructure.vulnerable.only')"
              @change='toggleChanged'
            ></v-switch>
          </div>
          <div slot="content">
            <slot name="tooltip">
              {{$t('infrastructure.vulnerable.only.tooltip')}}
            </slot>
          </div>
        </custom-tooltip>
        <custom-tooltip popper-class="light-tooltip">
          <div slot='buttonContent'>
            <v-switch
              class='filter-switch'
              v-model="hideUnresolved"
              :label="$t('infrastructure.hide.unresolved')"
              @change='toggleChanged'
            ></v-switch>
          </div>
          <div slot="content">
            <slot name="tooltip">
              {{$t('infrastructure.hide.unresolved.tooltip')}}
            </slot>
          </div>
        </custom-tooltip>
        <custom-tooltip popper-class="light-tooltip">
          <div slot='buttonContent'>
            <v-switch
              class='filter-switch'
              v-model="onlyNew"
              :label="$t('infrastructure.show.new.only')"
              @change='toggleChanged'
            ></v-switch>
          </div>
          <div slot="content">
            <slot name="tooltip">
              {{$t('infrastructure.show.new.only.tooltip')}}
            </slot>
          </div>
        </custom-tooltip>
        <custom-tooltip popper-class="light-tooltip">
          <div slot='buttonContent'>
            <v-switch
              class='filter-switch'
              v-model="onlyManual"
              :label="$t('infrastructure.show.manual.only')"
              @change='toggleChanged'
            ></v-switch>
          </div>
          <div slot="content">
            <slot name="tooltip">
              {{$t('infrastructure.show.manual.only.tooltip')}}
            </slot>
          </div>
        </custom-tooltip>
        <custom-tooltip popper-class="light-tooltip">
          <div slot='buttonContent'>
            <v-switch
              class='filter-switch'
              v-model="showOnlyRepetitive"
              :label="$t('configuration.only.repetitive')"
              @change='toggleChanged'
            ></v-switch>
          </div>
          <div slot="content">
            <slot name="tooltip">
              {{$t('infrastructure.show.repetitive.only.tooltip')}}
            </slot>
          </div>
        </custom-tooltip>
      </div>
    </div>
    <configuration-item-header
      tab="subdomains"
      :test-options="testItems"
      :url-count="selectedItemsCount"
      :selected-items="selectedItems"
      :all-items-count="assetsPaginated.length"
      :assets='assetsPaginated'
      :selectAllLabel="$t('configuration.infrastructure.assets.selected.subdomains')"
      @select-all="selectAllItems"
    >
      <template slot="additional-buttons">
        <div class='infrastructure__header'>
          <div class='priority-sort' @click='applyDefaultSorting'>
            <v-icon>sort</v-icon>
            <span> {{ $t('show.prioritized.issues.view.label') }} </span>
            <v-icon small>{{ iconForPrioritySorting }}</v-icon>
          </div>
          <v-btn class="add-subdomain-btn" @click="showPopup = true">
            {{ $t('configuration.add.asset') }}
          </v-btn>
          <v-btn class="add-tags" @click="openTagsPopup" :disabled="addTagsDisabled">
            {{ $t('configuration.add.tags') }}
          </v-btn>
          <download-c-s-v class="download-csv-btn" @click="downloadCSVReport"/>
        </div>
      </template>
    </configuration-item-header>
    <div class='infrastructure-content' v-if='displayLoadingAnimation'>
        <div v-if='assetsPaginated.length > 0'>
          <infrastructure-item-row
            :tab='currentTab'
            v-for="asset in assetsPaginated"
            :website="asset"
            :is-manual='asset.isManual'
            :key="asset.subdomain"
            :is-selected="isSelected(asset)"
            :current-selected-tld="asset.tld"
            :testTypes="testItemsInfrastructureFormatted"
            :amountOfTestCardInRow='amountOfTestCardInRow'
            :display-headers='checkIfHeadersDisabled(asset.subdomain)'
            :add-test-card-disabled='isAddTestDisabled(asset)'
            :organization='getOrganization(asset)'
            :sortDirection='getSortDirection'
            :sortOption='getSortOption'
            :clickedHeaderValue="clickedHeaderValue"
            @checked="checkedValue"
            @enableSubdomain="enableSubdomain"
            @commitHijackActionAsset="commitHijackActionAsset"
            @commitWafActionAsset="commitWafActionAsset"
            @toggleTechnologyDescriptionPopup="toggleTechnologyDescriptionPopup"
            @displayTestCardError='displayTestCardError'
            @openScreenshotPopup='openScreenshotPopup'
            @openMoveSubdomainPopup='openMoveSubdomainPopup'
            @openMoveIpPopup='openMoveIpPopup'
            @openAddTestPopup='openAddTestPopup'
            @openSuppressSubdomainPopup='openSuppressSubdomainPopup'
            @openSuppressPortsPopup='openSuppressPortsPopup'
            @openRemoveAssetPopup='openRemoveAssetPopup'
            @openSitemapPopup='openSitemapPopup'
            @openTagsPopup='openTagsPopup'
            @openWafPopup='openWafPopup'
            @submitTagsInformation='submitTagsInformation'
            @loadInfrastructure='loadInfrastructure'
            @headerClicked='headerClicked'
            @openSensitivityPopup='openSensitivityPopup'
            @addTagFilter='addTagFilter'
            @openDeleteAutoTagPopup='openDeleteAutoTagPopup'
          >
            <template slot="control">
              <custom-tooltip
                v-if="!asset.metaData || !asset.metaData.length"
                popper-class="light-tooltip"
                :content="$t('configuration.subdomain.list.suppress.tooltip')"
              >
                <v-btn slot="buttonContent" class="suppress-btn" @click.stop="suppressSubdomain(asset.subdomain)">suppress</v-btn>
              </custom-tooltip>
              <v-btn
                v-else
                slot="buttonContent"
                class="enable-btn"
                @click.stop="enableSubdomain(asset.subdomain)"
              >
                enable
              </v-btn>
            </template>
          </infrastructure-item-row>
        </div>
        <no-data
          v-else
          :message='messageForNoData'
        />
        <div class="infrastructure__footer">
          <advanced-pagination
            tab='infrastructure'
            :pages-amount='pages'
            :page="page"
            :is-show-pagination='isShowPagination'
            @changedSelectedPage='changedSelectedPage'
            @changedItemsAmountPerPage='changedItemsAmountPerPage'
          />
        </div>
      </div>
    <div class='infrastructure-tab-loading' v-else />
    <page-loading-overlay
      :show="infrastructureLoading && currentTab === 'infrastructure'"
      :progress="infrastructureProgress"
      contained
    />
    <add-asset-popup
      v-model="showPopup"
      :label="$t('configuration.asset.label')"
      :title="$t('configuration.add.asset')"
      :placeholder="$t('configuration.asset.placeholder')"
      :assets='assetsPaginated'
      :add-button-title="$t('configuration.add.asset')"
      @add="addNewItems"
      @upload="uploadNewItemsFromCSV"
    />
    <technology-description-right-popup
      class='technology-description-right-popup'
      :internal-value='technologyDescriptionPopup'
      :technology-item='currentTech'
      @closed='technologyDescriptionPopup = false'
    />
    <screenshot-popup
      :visible-popup='screenshotPopup'
      :screenshot-focused-index='screenshotIndexToDisplay'
      :screenshots='screenshots'
      @close='closeScreenshotPopup'
    />
    <edit-tld-popup v-if='subdomainToMove && (!subdomainToMove.configData.startsWith("n.a") || assetsPaginated.get(subdomainToMove.configData).isManual)'
        v-model="moveSubdomainPopup"
        :visiblePopup="moveSubdomainPopup"
        title="configuration.edit.tld"
        :save-button-title="$t('configuration.save')"
        :currentSelectedTld='currentSelectedTld'
        :item="subdomainToMove"
        @cancel='moveSubdomainPopup = false'
        @changed="moveSubdomain"
    />
    <move-ip-popup
     :visiblePopup="moveIpPopup"
     :currentSelectedTld='currentSelectedTld'
     :currentSelectedSubdomain='currentSubdomainForMoveIp'
     :assetsOptions='Array.from(assetsPaginated.keys())'
     :ipList='manualHostsKeys'
     title="infrastructure.move.ip"
     :save-button-title="$t('configuration.save')"
     @cancel='moveIpPopup = false'
     @changed="moveIp"
    />
    <add-test-popup
      v-model="addTestPopup"
      :website="assetForNewTest"
      :tab="'infrastructure'"
      :tld="tld"
      :organization='organization'
      :testTypes="currentTestTypesForNewTest"
      :hostList="hostsForNewTest"
      :ports-to-suggest='portsToSuggest'
      @submit="addTest"
    />
    <yes-no-popup
      class='suppress-subdomains-popup'
      :visible-popup="suppressSubdomainPopup"
      :title="'infrastructure.assets.suppress.domain.popup.title'"
      :message="'infrastructure.assets.suppress.domain.popup.alert'"
      :no-caption="'confirm.cancel'"
      :yes-caption="'confirm.ok'"
      @clicked="initiateActionOnSuppressSubdomain"
    />
    <yes-no-popup
      class='remove-asset-popup'
      :visible-popup="removeAssetPopup"
      :title="'infrastructure.assets.remove.asset.popup.title'"
      :message="'infrastructure.assets.remove.asset.popup.alert'"
      :no-caption="'confirm.cancel'"
      :yes-caption="'confirm.ok'"
      @clicked="initiateActionOnRemoveAsset"
    />
    <suppress-ports-popup
      v-if='hostsForSuppress.length'
      :value='editPortsPopup'
      :hosts='hostsForSuppress'
      @input='editPortsPopup = false'
    />
    <sitemap-popup
      :visible-popup='sitemapPopup'
      :data='activeSitemap'
      :website='activeSitemapWebsite'
      :features='activeFeatures'
      @close="closeSitemapPopup"
    />
    <select-integration-popup
      :value='selectIntegrationPopup'
      :website='currentWafActionPayload'
      @submitSelectedIntegration='submitSelectedIntegration'
      @toggleSelectIntegrationPopup='closeSelectIntegrationPopup'
      />
    <yes-no-popup
      class='toggle-waf-protection-popup'
      :visible-popup="toggleWafProtectionPopup"
      :title="toggleWafProtectionTitle"
      :message="toggleWafProtectionPopupMessage"
      :no-caption="'confirm.cancel'"
      :yes-caption="'confirm.ok'"
      @clicked="initiateToggleWafAction"
    />
    <error-message :errorText="errorText" @close="errorText = ''" />
    <change-sensitivity-popup
      v-if='sensitivityPopup'
      :visible-popup='sensitivityPopup'
      :sensitivityIndex='getSensitivityIndex()'
      @close='sensitivityPopup = false'
      @updateSensitivity='updateSensitivity'
    />
    <yes-no-popup
      width='450px'
      :visible-popup="deleteAutoTagPopup"
      :title="'delete.auto.tag.popup.title'"
      :message="'delete.auto.tag.popup.message'"
      :no-caption="'confirm.cancel'"
      :yes-caption="'confirm.ok'"
      @clicked="deleteAutoTag"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { TEST_TYPES_HOSTS, TEST_TYPES_SUBDOMAINS } from '@/constants/projects';
import {
  CONFIGURATION,
  CONFIGURATION_ACTION_TYPES,
  INFRASTRUCTURE_HEADERS,
} from '@/constants/configuration';
import CustomTooltip from '@/components/common/CustomTooltip.vue';
import ConfigurationItemHeader from '@/components/configuration/ConfigurationItemHeader.vue';
import {
  ADD_NEW_TEST_PROJECT,
  ADD_URL_CONFIGURATIONS,
  DELETE_CONFIGURATION,
  GET_PORTS_TO_SCORE,
  INITIATE_HIJACKED_ACTION_ASSET,
  INITIATE_TEST_CARD,
  INITIATE_WAF_ACTION_ASSET,
  LOAD_TEST_CARDS_HOSTS,
  LOAD_TEST_CARDS_SUBDOMAINS,
} from '@/store/project/actionTypes';
import DownloadCSV from '@/assets/icons/download_csv.svg?inline';
import InfrastructureItemRow from '@/components/configuration/InfrastructureItemRow.vue';
import AddAssetPopup from '@/components/popup/AddAssetPopup.vue';
import FilteredSearch from '@/components/common/FilteredSearch.vue';
import TechnologyDescriptionRightPopup from '@/components/popup/TechnologyDescriptionRightPopup.vue';
import ErrorMessage from '@/components/common/ErrorMessage.vue';
import PageLoadingOverlay from '@/components/common/PageLoadingOverlay.vue';
import { UPDATE_ITEMS_PER_PAGE } from '@/store/pagination/actionTypes';
import AdvancedPagination from '@/components/common/AdvancedPagination.vue';
import ScreenshotPopup from '@/components/popup/ScreenshotPopup.vue';
import EditTldPopup from '@/components/popup/EditTldPopup.vue';
import MoveIpPopup from '@/components/popup/MoveIpPopup.vue';
import AddTestPopup from '@/components/popup/AddTestPopup.vue';
import YesNoPopup from '@/components/popup/YesNoPopup.vue';
import SuppressPortsPopup from '@/components/popup/SuppressPortsPopup.vue';
import SitemapPopup from '@/components/popup/SitemapPopup.vue';
import SelectIntegrationPopup from '@/components/popup/SelectIntegrationPopup.vue';
import { LOAD_INTEGRATIONS_BY_CATEGORY } from '@/store/integration/actionTypes';
import {CHANGE_INFRASTRUCTURE_LIST_OPTIONS, UPDATE_SELECTED_INFRASTRUCTURE} from '@/store/configuration/actionTypes';
import { getGroupedTimezonesList } from '@/utils/dateUtils';
import moment from 'moment';
import NoData from '@/components/common/NoData.vue';
import {UPDATE_SENSITIVITY} from '@/store/user/actionTypes';
import ChangeSensitivityPopup from '@/components/popup/ChangeSensitivityPopup.vue';
import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';
import {downloadCSVReportByTab} from '@/services/repository';

export default {
  name: 'InfrastructureTab',
  mixins: [disableOuterScrollOnPopupVisible],
  props: {
    currentTab: {
      required: false,
    },
    amountOfTestCardInRow: {
      type: Number,
      required: false,
      default: 5,
    },
    hasFilter: {
      type: Boolean,
      required: false,
      default: false,
    },
    forceResetSelectedItemsInfrastructure: {
      type: Boolean,
      required: false,
      default: false,
    },
    forceLoadInfrastructure: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      isShowOnlyPorts: true,
      isShowOnlyVulnerable: false,
      hideUnresolved: true,
      onlyNew: false,
      onlyManual: false,
      showOnlyRepetitive: false,
      sortInfrastructure: {
        [INFRASTRUCTURE_HEADERS['infrastructure.tab.default.header']]: 'desc',
      },
      headersForTab: [
        'infrastructure.tab.sensitivity.header',
        'infrastructure.tab.main.asset.header',
        'infrastructure.tab.hosts.and.ports.header',
        'infrastructure.tab.services.header',
        'infrastructure.tab.waf.header',
        'infrastructure.tab.screenshots.header',
        'infrastructure.tab.technologies.header',
        'infrastructure.tab.last.scanned.header',
      ],
      clickedHeaderValue: {},
      selectedTestOption: '',
      showPopup: false,
      selectedItems: [],
      excludedItems: [],
      isAllSelected: false,
      filterItems: [],
      filters: [
        {name: 'Port', attr: 'port'},
        { name: 'Select Port', attr: 'selectPort' },
        {name: 'Exclude Port', attr: 'notport'},
        {name: 'IP', attr: 'ip'},
        {name: 'Technology', attr: 'tech'},
        {name: 'Tag', attr: 'tag'},
        {name: 'WAF', attr: 'WAF'},
        {name: 'Service', attr: 'service'},
        {name: 'Sensitivity', attr: 'sensitivity'},
        {name: 'Last Scanned Date', attr: 'lastScannedDate'},
      ],
      search: '',
      filterSearch: [],
      currentTech: { name: '' },
      technologyDescriptionPopup: false,
      errorText: '',
      screenshotPopup: false,
      moveSubdomainPopup: false,
      subdomainToMove: null,
      moveIpPopup: false,
      addTestPopup: false,
      manualHostsKeys: [],
      currentTestTypesForNewTest: [],
      assetForNewTest: '',
      hostsForNewTest: [],
      ipToMove: null,
      suppressSubdomainPopup: false,
      removeAssetPopup: false,
      assetToRemove: '',
      assetToRemoveTld: '',
      assetToRemoveMetaData: [],
      sitemapPopup: false,
      selectIntegrationPopup: false,
      currentWafActionPayload: {},
      activeSitemap: {},
      activeFeatures: {},
      activeSitemapWebsite: '',
      currentTldForSuppressSubdomain: '',
      subdomainToSuppress: '',
      subdomainToSuppressMetaData: [],
      currentSelectedTld: '',
      currentSubdomainForMoveIp: '',
      editPortsPopup: false,
      screenshotIndexToDisplay: 0,
      screenshots: [],
      hostsForSuppress: [],
      page: 1,
      portsToSuggest: [],
      toggleWafProtectionPopup: false,
      wafActionDetails: {},
      tld: '',
      organization: '',
      polling: null,
      sensitivityPopup: false,
      assetToUpdateSensitivity: {},
      defaultSortClicks: 1,
      approveLoadingOnSearchChange: false,
      filtersForPayload: {
        search: '',
        filter: [],
        toggles: {
          hasPorts: true,
          hasIssue: false,
          hideUnresolved: true,
          onlyNew: false,
          onlyManual: false,
        },
      },
      firstTimeCalled: false,
      deleteAutoTagPopup: false,
      autoTagDataToDelete: {},
    };
  },
  components: {
    NoData,
    SelectIntegrationPopup,
    SitemapPopup,
    DownloadCSV,
    AdvancedPagination,
    ScreenshotPopup,
    PageLoadingOverlay,
    TechnologyDescriptionRightPopup,
    FilteredSearch,
    AddAssetPopup,
    CustomTooltip,
    InfrastructureItemRow,
    ConfigurationItemHeader,
    ErrorMessage,
    EditTldPopup,
    MoveIpPopup,
    AddTestPopup,
    YesNoPopup,
    SuppressPortsPopup,
    ChangeSensitivityPopup,
  },
  async created() {
    if (this.currentTab === 'infrastructure') {
      await this.loadTestCardsSubdomains();
      await this.loadTestCardsHosts();
    }
    await this.$store.dispatch(GET_PORTS_TO_SCORE, {color: true});
  },
  async beforeDestroy() {
    this.clearInterval();
  },
  computed: {
    ...mapGetters([
      'dataLoading',
      'infrastructureLoading',
      'infrastructureRunningInterval',
      'domains',
      'testTypes',
      'domainFilter',
      'paginationItemsPerPageByTabs',
      'integrationsByCategory',
      'portsToScoreMap',
      'licenses',
      'amountOfRunningProjects',
      'domainsPerOrganization',
      'configurationDomains',
      'companyData',
      'organizationFilter',
      'infrastructureItems',
      'infrastructurePaginationState',
      'infrastructureRefId',
      'infrastructureProgress',
    ]),
    displayLoadingAnimation() {
      return !this.dataLoading && !this.infrastructureLoading;
    },
    iconForPrioritySorting() {
      return this.defaultSortClicks % 2 === 0 ? 'arrow_upward' : 'arrow_downward';
    },
    sensitivityOptions() {
      return Object.values(CONFIGURATION.InfrastructureSensitivityOptions);
    },
    getSortDirection() {
      return Object.values(this.sortInfrastructure)?.includes('desc') ? 'keyboard_arrow_down' : 'keyboard_arrow_up';
    },
    getSortOption() {
      return this.headersForTab.filter(header => Object.keys(this.sortInfrastructure)[0] === INFRASTRUCTURE_HEADERS[header])[0] || '';
    },
    messageForNoData() {
      return this.hasFilter
        ? this.$t('infrastructure.assets.no.data.message')
        : this.$t('no.filter.infrastructure.no.data.message');
    },
    severityToSortingScore() {
      return {
        critical: 10000000,
        high: 10000,
        medium: 100,
        low: 1,
      };
    },
    formattedTimeZone() {
      return getGroupedTimezonesList().map(tz => ({
        value: tz.offset,
        name: tz.name,
        id: tz.id,
      }));
    },
    selectedTimeZone() {
      return this.formattedTimeZone.find(timezone => timezone.value === String(moment().utcOffset()));
    },
    toggleWafProtectionTitle() {
      return this.wafActionDetails.action === CONFIGURATION.INFRASTRUCTURE_WAF_ACTIONS_TYPES.protect
        ? 'infrastructure.assets.protect.waf.popup.title'
        : 'infrastructure.assets.release.waf.popup.title';
    },
    toggleWafProtectionPopupMessage() {
      return this.wafActionDetails.action === CONFIGURATION.INFRASTRUCTURE_WAF_ACTIONS_TYPES.protect
        ? 'infrastructure.assets.protect.waf.popup.message'
        : 'infrastructure.assets.release.waf.popup.message';
    },
    warningTooltip() {
      return this.$t('configuration.vulnerable.asset.tooltip');
    },
    hijackableTooltip() {
      return this.$t('configuration.infrastructure.assets.tooltip');
    },
    noPocTooltip() {
      return this.$t('configuration.infrastructure.assets.noPoc.tooltip');
    },
    itemsPerPage() {
      return this.paginationItemsPerPageByTabs.infrastructure;
    },
    testItemsSubdomainsFormatted() {
      return Object.values(TEST_TYPES_SUBDOMAINS);
    },
    testItemsHostsFormatted() {
      return Object.values(TEST_TYPES_HOSTS);
    },
    testItemsInfrastructureFormatted() {
      return this.testItemsHostsFormatted.concat(this.testItemsSubdomainsFormatted);
    },
    testItems() {
      const testItemsFormatted = this.testItemsHostsFormatted.concat(this.testItemsSubdomainsFormatted);
      return testItemsFormatted.map(test => ({ value: test, title: this.$t(`test.testType.${test}`) }));
    },
    selectedItemsCount() {
      if (this.isAllSelected) {
        return (this.filtersForPayload.total - this.excludedItems.length + this.selectedItems.length);
      }
      return this.selectedItems.length || 0;
    },
    assetsPaginated() {
      return this.infrastructureItems;
    },
    pages() {
      return this.infrastructurePaginationState?.pageCount || Math.ceil(this.infrastructureItems.length / this.itemsPerPage);
    },
    isShowPagination() {
      return this.pages > 1;
    },
    addTagsDisabled() {
      if (!this.isAllSelected) {
        return !this.selectedItems.length;
      }
      return false;
    },
  },
  methods: {
    async loadInfrastructure(turnOnLoading = true) {
      this.clearInterval();
      if (this.currentTab === 'infrastructure' && this.domainFilter.length) {
        await this.$store.dispatch(CHANGE_INFRASTRUCTURE_LIST_OPTIONS, {
          sort: this.sortInfrastructure,
          limit: this.itemsPerPage,
          page: this.page,
          search: this.search,
          filter: this.filterSearch,
          hasPorts: this.isShowOnlyPorts,
          hasIssue: this.isShowOnlyVulnerable,
          hideUnresolved: this.hideUnresolved,
          onlyNew: this.onlyNew,
          onlyManual: this.onlyManual,
          domain: this.domainFilter || [],
          organizations: this.organizationFilter || [],
          turnOnLoading,
          tab: 'infrastructure',
          selectAllFilters: {
            ...this.filtersForPayload, selected: this.selectedItems, excluded: this.excludedItems, isAllSelected: this.isAllSelected,
          },
        });
        if (this.infrastructureRefId && this.infrastructureRefId !== '') {
          this.polling = setInterval(async () => {
            if (!this.infrastructureRefId || this.infrastructureRefId === '') {
              this.clearInterval();
            } else if (!this.infrastructureRunningInterval) {
              await this.$store.dispatch(CHANGE_INFRASTRUCTURE_LIST_OPTIONS, {
                sort: this.sortInfrastructure,
                limit: this.itemsPerPage,
                page: this.page,
                search: this.search,
                filter: this.filterSearch,
                hasPorts: this.isShowOnlyPorts,
                hasIssue: this.isShowOnlyVulnerable,
                hideUnresolved: this.hideUnresolved,
                onlyNew: this.onlyNew,
                onlyManual: this.onlyManual,
                domain: this.domainFilter || [],
                organizations: this.organizationFilter || [],
                tab: 'infrastructure',
                refId: this.infrastructureRefId,
                selectAllFilters: {
                  ...this.filtersForPayload, selected: this.selectedItems, excluded: this.excludedItems, isAllSelected: this.isAllSelected,
                },
              });
            }
          }, 3000);
        }
      }
    },
    clearInterval() {
      clearInterval(this.polling);
      this.polling = null;
    },
    resetFiltersForPayload() {
      this.filtersForPayload = {
        search: this.search,
        filter: this.filterSearch,
        toggles: {
          hasPorts: this.isShowOnlyPorts,
          hasIssue: this.isShowOnlyVulnerable,
          hideUnresolved: this.hideUnresolved,
          onlyNew: this.onlyNew,
          onlyManual: this.onlyManual,
        },
        total: this.infrastructurePaginationState.total,
      };
    },
    async loadTestCardsSubdomains() {
      if (this.currentTab === 'infrastructure') {
        await this.$store.dispatch(LOAD_TEST_CARDS_SUBDOMAINS, {
          onlyRepetitive: this.showOnlyRepetitive,
          subdomains: this.infrastructureItems.map(item => item.subdomain),
        });
      }
    },
    async loadTestCardsHosts() {
      if (this.currentTab === 'infrastructure') {
        let totalHosts = [];
        if (this.infrastructureItems) {
          const subdomainHosts = this.infrastructureItems.map(item => item.hosts);
          subdomainHosts.forEach(hosts => {
            if (hosts && hosts.length > 0) {
              const hostKeys = hosts.map(host => host.configData);
              totalHosts = [...totalHosts, ...hostKeys];
            }
          });
        }
        await this.$store.dispatch(LOAD_TEST_CARDS_HOSTS, {
          onlyRepetitive: this.showOnlyRepetitive,
          hosts: Array.from(new Set(totalHosts)),
        });
      }
    },
    async applyDefaultSorting() {
      this.defaultSortClicks += 1;
      const payload = {
        header: 'infrastructure.tab.default.header',
        clicks: this.defaultSortClicks,
      };
      await this.headerClicked(payload);
    },
    async headerClicked(payload) {
      const sortDirection = payload.clicks % 2 === 0 ? 'asc' : 'desc';
      const sortBy = INFRASTRUCTURE_HEADERS[payload.header];
      this.sortInfrastructure = {
        [sortBy]: sortDirection,
      };
      this.clickedHeaderValue = {
        [payload.header]: payload.clicks,
      };
    },
    getOrganization(item) {
      return Array.from(this.domainsPerOrganization.entries())
        .find(([, domainList]) => domainList.includes(item.tld))?.[0] || '';
    },
    isAddTestDisabled(item) {
      const {organization} = item;
      const companyName = organization?.name || this.companyData.name;
      return !this.licenses[companyName]?.length && this.amountOfRunningProjects[companyName] > 2;
    },
    isNum(value) {
      return /^\d+$/.test(value);
    },
    hasPorts(host) {
      return host?.hosts?.some(asset => asset?.ports?.length > 0);
    },
    async downloadCSVReport() {
      const payload = {
        sort: this.sortInfrastructure,
        limit: this.itemsPerPage,
        page: this.page,
        search: this.search,
        filter: this.filterSearch,
        toggles: {
          hasPorts: this.isShowOnlyPorts,
          hasIssue: this.isShowOnlyVulnerable,
          hideUnresolved: this.hideUnresolved,
          onlyNew: this.onlyNew,
          onlyManual: this.onlyManual,
        },
        domain: this.domainFilter || [],
        organizations: this.organizationFilter || [],
        tab: 'infrastructure',
      };
      const dataStream = await downloadCSVReportByTab(payload);
      const blob = new Blob([dataStream], { type: 'text/plain' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute('download', `${Date.now()}-infrastructure.csv`);
      document.body.appendChild(link);
      link.click();
    },
    async changedItemsAmountPerPage(itemsPerPage) {
      window.scrollTo(0, 0);
      this.page = 1;
      await this.$store.dispatch(UPDATE_ITEMS_PER_PAGE, { itemsPerPage, tab: 'infrastructure'});
      await this.loadInfrastructure();
    },
    async changedSelectedPage(currentPage) {
      window.scrollTo(0, 0);
      this.page = currentPage;
      await this.loadInfrastructure();
    },
    getPortFromUrl(url) {
      const port = url?.split('_')?.slice(-1)[0]?.split('.')[0] || '';
      if (port && port.length > 0 && this.isNum(port)) {
        return port;
      }
      const assetPart = url?.split('/')?.slice(-1)[0] || '';
      if (assetPart.startsWith('https')) {
        return '443';
      }
      if (assetPart.startsWith('http')) {
        return '80';
      }
      return '';
    },
    checkIfHeadersDisabled(assetKey) {
      return this.assetsPaginated?.[0].subdomain === assetKey || false;
    },
    displayTestCardError(errorMessage) {
      this.errorText = errorMessage;
    },
    openMoveSubdomainPopup(payload) {
      this.moveSubdomainPopup = true;
      this.subdomainToMove = {configData: payload.website};
      this.currentSelectedTld = payload.currentSelectedTld;
    },
    openSuppressSubdomainPopup(payload) {
      this.suppressSubdomainPopup = true;
      this.subdomainToSuppress = payload.website;
      this.subdomainToSuppressMetaData = payload.metaData;
      this.currentTldForSuppressSubdomain = payload.tld;
    },
    openRemoveAssetPopup(payload) {
      this.removeAssetPopup = true;
      this.assetToRemove = payload.website;
      this.assetToRemoveTld = payload.tld;
      this.assetToRemoveMetaData = payload.metaData || [];
    },
    openSuppressPortsPopup(payload) {
      this.editPortsPopup = true;
      this.hostsForSuppress = payload.hosts;
    },
    openMoveIpPopup(payload) {
      this.ipToMove = payload.website;
      this.currentSelectedTld = payload.currentSelectedTld;
      this.manualHostsKeys = payload.website.hosts.filter(host => host.isManual).map(item => item.configData);
      this.currentSubdomainForMoveIp = this.ipToMove.subdomain;
      this.moveIpPopup = true;
    },
    openAddTestPopup(payload) {
      const organization = this.configurationDomains.find(domain => domain.configData === (payload.tld || ''))?.organization || {};
      const payloadWithOrg = {...payload, organization };
      if (!this.isAddTestDisabled(payloadWithOrg)) {
        this.addTestPopup = true;
        this.currentTestTypesForNewTest = payload.testTypesFiltered;
        this.assetForNewTest = payload.websiteTitle;
        this.hostsForNewTest = payload.hosts;
        this.portsToSuggest = payload.ports || [];
        this.tld = payload.tld;
        this.organization = payloadWithOrg.organization;
      }
    },
    openSitemapPopup(payload) {
      this.sitemapPopup = true;
      this.activeSitemap = payload.sitemap || {};
      this.activeFeatures = payload.features || {};
      this.activeSitemapWebsite = payload.website || '';
    },
    closeSitemapPopup() {
      this.sitemapPopup = false;
      this.activeSitemap = {};
      this.activeFeatures = {};
      this.activeSitemapWebsite = '';
    },
    closeSelectIntegrationPopup() {
      this.selectIntegrationPopup = false;
      this.currentWafActionPayload = {};
    },
    openScreenshotPopup(payload) {
      this.screenshots = payload.screenshots;
      this.screenshotIndexToDisplay = payload.focusedIndex;
      this.screenshotPopup = true;
    },
    closeScreenshotPopup() {
      this.screenshotIndexToDisplay = 0;
      this.screenshotPopup = false;
    },
    toggleTechnologyDescriptionPopup(tech) {
      if (!this.technologyDescriptionPopup) {
        this.currentTech.name = tech;
        this.technologyDescriptionPopup = true;
      } else {
        this.technologyDescriptionPopup = false;
        this.currentTech.name = tech;
        this.technologyDescriptionPopup = true;
      }
    },
    async commitHijackActionAsset(payload) {
      try {
        await this.$store.dispatch(INITIATE_HIJACKED_ACTION_ASSET, payload);
      } catch (e) {
        this.errorText = e.message;
      }
    },
    async submitSelectedIntegration(selectedIntegration) {
      this.currentWafActionPayload = {
        ...this.currentWafActionPayload,
        wafVendor: selectedIntegration,
        timezone: this.selectedTimeZone.id,
      };
      try {
        await this.$store.dispatch(INITIATE_WAF_ACTION_ASSET, this.currentWafActionPayload);
        await this.loadInfrastructure(false);
      } catch (e) {
        this.errorText = e.message;
      }
    },
    async commitWafActionAsset(payload) {
      await this.$store.dispatch(LOAD_INTEGRATIONS_BY_CATEGORY, { category: 'Waf' });
      this.currentWafActionPayload = payload;
      if (this.integrationsByCategory.length === 1) {
        await this.submitSelectedIntegration(this.integrationsByCategory[0].name);
      } else {
        this.selectIntegrationPopup = true;
      }
    },
    async searchChange(e) {
      this.search = e.trim();
      this.search = this.extractSearchString();
      this.page = 1;
      if (this.approveLoadingOnSearchChange || this.search.length) {
        await this.loadInfrastructure();
        await this.loadTestCardsSubdomains();
        await this.loadTestCardsHosts();
      }
      if (!this.approveLoadingOnSearchChange) {
        this.approveLoadingOnSearchChange = true;
      }
    },
    async filtersChange(e) {
      this.filterSearch = e;
      this.filterSearch = this.extractFilters();
      this.page = 1;

      await this.loadInfrastructure();
      await this.loadTestCardsSubdomains();
      await this.loadTestCardsHosts();
    },
    extractSearchString() {
      if (!this.search) {
        return '';
      }
      let results = this.search.split(' ');
      results = results.filter(result => !result.includes(':'));
      return results.join(' ').trim();
    },
    extractFilters() {
      const filtersMap = new Map();
      if (this.search) {
        let results = this.search.split(' ');
        results = results.filter(result => result.includes(':'));
        results.forEach(item => {
          const filter = item.split(':');
          if (filter.length > 1 && filter[1].length > 0) {
            filtersMap.set(filter[0], filter[1].split(',').map(element => element.trim()));
          }
        });
      }
      if (this.filterSearch.length > 0) {
        this.filterSearch.forEach(item => {
          let filter = item.split(':');
          if (filter[0] === 'ip') {
            filter = item.split(/:(.*)/s).slice(0, 2);
          }
          if (filtersMap.has(filter[0])) {
            const filtersValues = filtersMap.get(filter[0]);
            filter[1].split(',').forEach(value => {
              filtersValues.push(value.trim());
            });
            filtersMap.set(filter[0], filtersValues);
          } else {
            const filtersValues = [];
            filter[1].split(',').forEach(value => {
              filtersValues.push(value.trim());
            });
            filtersMap.set(filter[0], filtersValues);
          }
        });
      }
      return filtersMap;
    },
    addTagFilter(tagKey) {
      this.filterItems = [`tag: ${tagKey}`];
    },
    openDeleteAutoTagPopup(payload) {
      this.autoTagDataToDelete = {...payload};
      this.deleteAutoTagPopup = true;
    },
    async addNewItems(payload) {
      const configData = payload.items.map(item => ({
        data: item.url, metaData: [], tld: item.tld, subdomain: item.subdomain || null,
      }));
      try {
        await this.$store.dispatch(ADD_URL_CONFIGURATIONS, {
          payload: configData,
          configType: payload.configType,
          successMessageType: CONFIGURATION_ACTION_TYPES.ADD_ASSET,
          runIpResolver: payload.runIpResolver || false,
        });
        await this.loadInfrastructure();
      } catch (e) {
        this.errorText = e.message || e;
      }
    },
    async uploadNewItemsFromCSV(payload) {
      if (payload.items) {
        const reader = new FileReader();
        reader.readAsText(payload.items);
        reader.onload = res => {
          this.$store.dispatch(ADD_URL_CONFIGURATIONS, {
            payload: [res.target.result],
            tld: payload.tld,
            configType: payload.configType,
            successMessageType: CONFIGURATION_ACTION_TYPES.IMPORT_ASSETS_FROM_CSV,
            runIpResolver: payload.runIpResolver || false,
          });
        };
        await this.loadInfrastructure();
      }
    },
    async moveSubdomain(selectedDomain) {
      await this.$store.dispatch(ADD_URL_CONFIGURATIONS, {
        payload: [{ data: this.subdomainToMove.configData, metaData: [], tld: selectedDomain }],
        configType: CONFIGURATION.CONFIG_TYPES.SUBDOMAIN,
        successMessageType: CONFIGURATION_ACTION_TYPES.MOVE_SUBDOMAIN,
      });
      await this.loadInfrastructure();
    },
    async moveIp(data) {
      await this.$store.dispatch(ADD_URL_CONFIGURATIONS, {
        payload: [{
          data: data.host, metaData: [], tld: data.tld, subdomain: data.subdomain,
        }],
        configType: CONFIGURATION.CONFIG_TYPES.HOST,
        successMessageType: CONFIGURATION_ACTION_TYPES.MOVE_IP,
      });
      await this.loadInfrastructure();
    },
    async suppressSubdomain(payload) {
      await this.$store.dispatch(ADD_URL_CONFIGURATIONS, {
        payload: [{ data: payload.data, metaData: [...payload.metaData, payload.data], tld: payload.tld }],
        configType: payload.configType,
        successMessageType: CONFIGURATION_ACTION_TYPES.SUPPRESS_SUBDOMAIN,
      });
      await this.loadInfrastructure();
    },
    async enableSubdomain(payload) {
      const updatedMetaData = payload.metaData.filter(item => item !== payload.data) || [];
      await this.$store.dispatch(ADD_URL_CONFIGURATIONS, {
        payload: [{ data: payload.data, metaData: [...updatedMetaData], tld: payload.tld }],
        configType: payload.configType,
        successMessageType: CONFIGURATION_ACTION_TYPES.ENABLE_SUBDOMAIN,
      });
      await this.loadInfrastructure();
    },
    async initiateActionOnSuppressSubdomain(value) {
      if (value) {
        await this.suppressSubdomain({
          configType: CONFIGURATION.CONFIG_TYPES.SUBDOMAIN,
          data: this.subdomainToSuppress,
          tld: this.currentTldForSuppressSubdomain,
          metaData: this.subdomainToSuppressMetaData,
        });
      }
      this.suppressSubdomainPopup = false;
    },
    async removeAsset(payload) {
      await this.$store.dispatch(DELETE_CONFIGURATION, {
        payload: [{ data: payload.data, metaData: payload.metaData || [], tld: payload.tld }],
        configType: CONFIGURATION.CONFIG_TYPES.SUBDOMAIN,
        successMessageType: CONFIGURATION_ACTION_TYPES.REMOVE_ASSET,
      });
      await this.loadInfrastructure();
    },
    async initiateActionOnRemoveAsset(value) {
      if (value) {
        try {
          await this.removeAsset({
            data: this.assetToRemove,
            tld: this.assetToRemoveTld,
            metaData: this.assetToRemoveMetaData,
          });
        } catch (e) {
          this.errorText = e.message || e;
        }
      }
      this.removeAssetPopup = false;
    },
    async addTest(test) {
      const testWithTld = test;
      if (Object.values(TEST_TYPES_HOSTS).includes(test.type)) {
        testWithTld.tld = this.tld;
      }
      try {
        const createdTest = await this.$store.dispatch(ADD_NEW_TEST_PROJECT, testWithTld);
        if (!testWithTld.schedule && createdTest) {
          await this.$store.dispatch(INITIATE_TEST_CARD, [createdTest.id]);
        }
      } catch (e) {
        this.errorText = e.message;
      }
      await this.loadTestCardsSubdomains();
      await this.loadTestCardsHosts();
    },
    checkedValue(item) {
      if (this.isAllSelected) {
        if (item?.selected !== undefined) {
          if (item.selected) {
            const index = this.excludedItems.findIndex(data => data === item.subdomain);
            if (index !== -1) {
              this.excludedItems.splice(index, 1);
            } else {
              this.excludedItems.push(item.subdomain);
            }
          } else {
            const index = this.selectedItems.findIndex(data => data === item.subdomain);
            if (index !== -1) {
              this.selectedItems.splice(index, 1);
            } else {
              this.selectedItems.push(item.subdomain);
            }
            if (this.excludedItems.includes(item.subdomain)) {
              const excludeIndex = this.excludedItems.findIndex(data => data === item.subdomain);
              this.excludedItems.splice(excludeIndex, 1);
              this.filtersForPayload.total -= 1;
            }
          }
        } else {
          const index = this.excludedItems.findIndex(data => data === item.subdomain);
          if (index !== -1) {
            this.excludedItems.splice(index, 1);
          } else {
            this.excludedItems.push(item.subdomain);
          }
        }
      } else {
        const index = this.selectedItems.findIndex(data => data === item.subdomain);
        if (index !== -1) {
          this.selectedItems.splice(index, 1);
        } else {
          this.selectedItems.push(item.subdomain);
        }
      }
    },
    selectAllItems() {
      this.isAllSelected = !this.isAllSelected;
      if (this.selectedItems.length) {
        this.selectedItems = [];
      }
      if (this.excludedItems.length) {
        this.excludedItems = [];
      }
      this.resetFiltersForPayload();
    },
    isSelected(asset) {
      return this.selectedItems.includes(asset.subdomain) ||
        (this.isAllSelected && asset?.selected !== undefined && asset.selected && !this.excludedItems.includes(asset.subdomain)) ||
        (this.isAllSelected && asset?.selected === undefined && !this.excludedItems.includes(asset.subdomain));
    },
    openTagsPopup(asset = []) {
      let selected = this.selectedItems;
      if (asset.length) {
        selected = asset;
      }
      const payload = {
        selected,
        excluded: this.excludedItems,
        isAllSelected: this.isAllSelected,
        search: this.filtersForPayload?.search || '',
        filter: Object.keys(this?.filtersForPayload)?.length ? JSON.stringify(Object.fromEntries(this.filtersForPayload.filter)) : {},
        toggles: {
          hasPorts: this.filtersForPayload?.toggles?.isShowOnlyPorts || true,
          hasIssue: this.filtersForPayload?.toggles?.isShowOnlyVulnerable || false,
          hideUnresolved: this.filtersForPayload?.toggles?.hideUnresolved || true,
          onlyNew: this.filtersForPayload?.toggles?.onlyNew || false,
          onlyManual: this.filtersForPayload?.toggles?.onlyManual || false,
        },
        domain: this.domainFilter || [],
        organizations: this.organizationFilter || [],
        tab: 'infrastructure',
      };
      this.$emit('toggleTagsPopup', payload);
    },
    openWafPopup(wafActionDetails) {
      this.wafActionDetails = wafActionDetails;
      this.toggleWafProtectionPopup = true;
    },
    initiateToggleWafAction(clickedValue) {
      if (clickedValue) {
        this.commitWafActionAsset(this.wafActionDetails);
      }
      this.toggleWafProtectionPopup = false;
    },
    deleteAutoTag(clickedValue) {
      if (clickedValue) {
        this.removeAutoTagFromList();
      } else {
        this.autoTagDataToDelete = {};
      }
      this.deleteAutoTagPopup = false;
    },
    async removeAutoTagFromList() {
      const {
        tag,
        currentAutoTags,
        currentAutoTagKeys,
        title,
        tld,
      } = this.autoTagDataToDelete;

      const tagsUpdated = {...currentAutoTags};

      if (currentAutoTagKeys.includes(tag)) {
        tagsUpdated[tag] = 'exclude';
      }

      await this.submitTagsInformation({
        action: CONFIGURATION_ACTION_TYPES.REMOVE_TAG,
        configType: CONFIGURATION.CONFIG_TYPES.TAGS_AUTO,
        items: [{
          url: title,
          tld,
          tags: { ...tagsUpdated },
        }],
      });
      this.autoTagDataToDelete = {};
    },
    async submitTagsInformation(payload) {
      await this.$emit('submitTagsInformation', { items: payload.items, configType: payload.configType, action: payload.action });
    },
    async toggleChanged() {
      this.page = 1;
      await this.loadInfrastructure();
      await this.loadTestCardsSubdomains();
      await this.loadTestCardsHosts();
    },
    async openSensitivityPopup(subdomain, tld, sensitivity) {
      this.assetToUpdateSensitivity = {subdomain, tld, sensitivity};
      this.sensitivityPopup = true;
    },
    async updateSensitivity(sensitivity) {
      await this.$store.dispatch(UPDATE_SENSITIVITY, {
        tld: this.assetToUpdateSensitivity.tld,
        subdomain: this.assetToUpdateSensitivity.subdomain,
        sensitivity,
        tab: 'infrastructure',
      });
      this.$emit('forceRerenderTopLevelDomainTab', true);
      this.sensitivityPopup = false;
      await this.loadInfrastructure();
    },
    getSensitivityIndex() {
      const index = this.sensitivityOptions.findIndex(item => item === this.assetToUpdateSensitivity?.sensitivity);
      // In case there is no sensitivity for domain, we will take CRITICAL as default
      return index < 0 ? 4 : index;
    },
    resetSelectedItems() {
      this.selectedItems = [];
      this.excludedItems = [];
      this.isAllSelected = false;
      this.filtersForPayload = {
        search: '',
        filter: [],
        toggles: {
          hasPorts: true,
          hasIssue: false,
          hideUnresolved: true,
          onlyNew: false,
          onlyManual: false,
        },
      };
    },
  },
  watch: {
    async currentTab() {
      if (this.currentTab === 'infrastructure' && this.domainFilter.length && !this.firstTimeCalled) {
        this.firstTimeCalled = true;
        await this.loadInfrastructure();
        await this.loadTestCardsSubdomains();
        await this.loadTestCardsHosts();
      }
      this.resetSelectedItems();
      this.technologyDescriptionPopup = false;
    },
    async domainFilter() {
      this.resetSelectedItems();
      if (this.currentTab !== 'infrastructure' && this.domainFilter.length) {
        this.firstTimeCalled = false;
      }
      if (this.currentTab === 'infrastructure' && this.domainFilter.length) {
        this.page = 1;
        this.approveLoadingOnSearchChange = false;
        this.approveLoadingOnFilterChange = false;
        this.search = '';
        this.filterSearch = [];
        this.filterItems = [];
        this.clearInterval();
        await this.loadInfrastructure();
        await this.loadTestCardsSubdomains();
        await this.loadTestCardsHosts();
      }
    },
    async sortInfrastructure() {
      this.page = 1;
      await this.loadInfrastructure();
      await this.loadTestCardsSubdomains();
      await this.loadTestCardsHosts();
    },
    sensitivityPopup() {
      this.disableOuterScrollWhenPopupOpen(this.sensitivityPopup);
    },
    excludedItems() {
      if (this.excludedItems.length === this.filtersForPayload.total && this.isAllSelected) {
        this.isAllSelected = false;
      }
    },
    selectedItems() {
      if (this.selectedItems.length === this.filtersForPayload.total && !this.isAllSelected) {
        this.isAllSelected = true;
      }
    },
    async isAllSelected() {
      if (!this.isAllSelected) {
        this.excludedItems = [];
        this.resetFiltersForPayload();
      }
      if (this.isAllSelected) {
        const items = [];
        this.infrastructureItems.forEach(item => {
          items.push({ ...item, selected: true });
        });
        await this.$store.dispatch(UPDATE_SELECTED_INFRASTRUCTURE, items);
        this.selectedItems = [];
        this.resetFiltersForPayload();
      }
    },
    forceResetSelectedItemsInfrastructure() {
      if (this.forceResetSelectedItemsInfrastructure) {
        this.resetSelectedItems();
      }
    },
    async forceLoadInfrastructure() {
      if (this.forceLoadInfrastructure) {
        await this.loadInfrastructure();
      }
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.infrastructure {
  padding: $space-4 $space-8;
  position: relative; // Required for contained loading overlay
  min-height: calc(100vh - 200px); // Ensure overlay has space to display

  &__headers {
    display: flex;
    margin-top: rem(-19);
    margin-left: rem(-1);
    margin-bottom: $space-2;

    &__filters_row {
      display: flex;
      width: 100%;

      .search-filter {
        flex: 50%;
      }

      .filter-switch {
        margin-left: $space-10;
      }
    }
  }

  &__header {
    display: flex;
    justify-content: flex-end;

    .priority-sort {
      border: 1px solid transparent;
      border-radius: $radius-md;
      height: rem(30);
      padding: $space-0-5;
      margin-right: $space-6;
      color: var(--text-primary);

      .v-icon {
        color: var(--text-primary) !important;
      }

      &:hover {
        cursor: pointer;
        background-color: var(--bg-tertiary);
      }
    }

    // Override vuetify default button background
    .add-subdomain-btn.add-subdomain-btn.add-subdomain-btn {
      background-color: var(--text-secondary) !important;
      border-radius: $radius-sm;
      box-shadow: $shadow-sm;
      color: #ffffff !important;
      font-family: $default-application-font-medium;
      font-size: $font-size-sm;
      height: $btn-height-md;
      letter-spacing: 1.1px;
      line-height: $font-size-md;
      width: rem(143);
      margin-right: $space-5;
      text-transform: uppercase;
    }

    .add-tags.add-tags {
      background-color: var(--text-secondary) !important;
      border: none !important;
      border-radius: $radius-sm;
      box-shadow: $shadow-sm;
      color: var(--text-inverse) !important;
      font-family: $default-application-font-medium;
      font-size: $font-size-sm;
      height: $btn-height-md;
      letter-spacing: 1.1px;
      line-height: $font-size-md;
      width: rem(143);
      margin-right: $space-2;
      text-transform: uppercase !important;
    }

    .download-csv-btn {
      color: var(--text-inverse);
      font-family: $default-application-font-medium;
      font-size: $font-size-sm;
      height: $btn-height-md;
      letter-spacing: 1.1px;
      line-height: $font-size-md;
      width: rem(78);

      &:hover {
        cursor: pointer;
      }
    }
  }

  .no-results {
    text-align: center;
    color: var(--text-secondary);
    font-family: $default-application-font-regular;
    font-size: $font-size-4xl;
    line-height: $space-10;
  }

  &__footer {
    margin-top: $space-6;
  }
}

.infrastructure-tab-loading {
  align-self: center;
  justify-self: center;
  margin-top: 20%;
}
</style>
